<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import ACTooltip from "../ACTooltip/ACTooltip.vue";
import {
  formLabelCva,
  formRequiredCva,
  formSubWrapperCva,
  formWrapperCva,
} from "../Form/form.cva";
import VIcon from "../Icon/VIcon.vue";
import VSkeleton from "../Skeleton/VSkeleton.vue";
import { useFieldId } from "../useFieldId";
import { useRadioGroup } from "../useRadioGroup";

interface Props {
  errorMessage?: string
  loading?: boolean
  label?: string
  labelPosition?: "top" | "left"
  required?: boolean
  placeholder?: string
  emptyValue?: string | null
  defaultColor?: string | null
  variant?: string
  size?: "medium" | "large"
  disabled?: boolean
  hasPerms?: boolean
  hasMargin?: boolean
  labelCenter?: boolean
  mode?: "picker" | "palette"
  presetColors?: string[]
  columns?: number
  ariaDescribedby?: string
  /**
   * Id of the color input, or of the swatch group in palette mode, for an external `<label for>`.
   * Defaults to the surrounding field's id or a unique id.
   */
  id?: string
}

const props = withDefaults(defineProps<Props>(), {
  errorMessage: "",
  loading: false,
  label: "",
  labelPosition: "top",
  required: false,
  placeholder: "placeholder",
  emptyValue: null,
  defaultColor: null,
  variant: "",
  size: "large",
  hasPerms: true,
  disabled: false,
  hasMargin: false,
  labelCenter: false,
  mode: "picker",
  presetColors: () => [
    "#ef4444",
    "#f97316",
    "#f59e0b",
    "#eab308",
    "#84cc16",
    "#22c55e",
    "#10b981",
    "#14b8a6",
    "#06b6d4",
    "#0ea5e9",
    "#3b82f6",
    "#6366f1",
    "#8b5cf6",
    "#a855f7",
    "#d946ef",
    "#ec4899",
    "#f43f5e",
    "#78716c",
    "#64748b",
    "#000000",
  ],
  columns: 10,
  ariaDescribedby: undefined,
  id: undefined,
});

defineEmits<{
  (e: "change"): void
  (e: "blur"): void
}>();

/** Translation keys under `base.colorLabel` of the default palette, so each swatch has a spoken name. */
const PRESET_COLOR_NAMES: Record<string, string> = {
  "#ef4444": "red",
  "#f97316": "orange",
  "#f59e0b": "amber",
  "#eab308": "yellow",
  "#84cc16": "lime",
  "#22c55e": "green",
  "#10b981": "emerald",
  "#14b8a6": "teal",
  "#06b6d4": "cyan",
  "#0ea5e9": "sky",
  "#3b82f6": "blue",
  "#6366f1": "indigo",
  "#8b5cf6": "violet",
  "#a855f7": "purple",
  "#d946ef": "fuchsia",
  "#ec4899": "pink",
  "#f43f5e": "rose",
  "#78716c": "stone",
  "#64748b": "slate",
  "#000000": "black",
};

const isDisabled = computed(() => {
  return props.disabled || !props.hasPerms;
});

const { t } = useI18n();

const modelValue = defineModel<string | null>({ default: null });
const id = useFieldId(() => props.id);
const labelId = computed(() => `${id.value}-label`);
const debounceTimeout = ref<number | null>(null);

function clearColor(event: Event) {
  event.stopPropagation();
  modelValue.value = props.emptyValue || "";
}

function selectColor(color: string) {
  if (isDisabled.value) {
    return;
  }
  modelValue.value = color;
}

/** A color outside the default palette has no name, so its value is read out instead. */
function colorName(color: string): string {
  const key = PRESET_COLOR_NAMES[color.toLowerCase()];
  return key ? t(`base.colorLabel.${key}`) : color;
}

const { setRadioRef, radioTabIndex, onRadioKeydown } = useRadioGroup(
  () => props.presetColors.map(color => ({ checked: modelValue.value === color, disabled: isDisabled.value })),
  (index) => {
    const color = props.presetColors[index];
    if (color) {
      selectColor(color);
    }
  },
);

function handleColorInput(e: Event) {
  const value = (e.target as HTMLInputElement).value;

  // Clear any existing timeout
  if (debounceTimeout.value) {
    window.clearTimeout(debounceTimeout.value);
  }

  // Set new timeout
  debounceTimeout.value = window.setTimeout(() => {
    modelValue.value = value;
  }, 100); // 100ms debounce
}

const displayValue = computed(() => {
  return modelValue.value || props.placeholder;
});

const isPlaceholder = computed(() => {
  return !modelValue.value;
});

const hasError = computed(() => props.errorMessage !== "");

const errorId = computed(() => `${id.value}-error`);

const computedAriaInvalid = computed(() => {
  if (props.errorMessage) {
    return "true";
  }
  return undefined;
});

const computedAriaDescribedby = computed(() => {
  const parts: string[] = [];
  if (props.ariaDescribedby) {
    parts.push(props.ariaDescribedby);
  }
  if (hasError.value) {
    parts.push(errorId.value);
  }
  if (parts.length === 0) {
    return undefined;
  }
  return parts.join(" ");
});

const layout = computed(() => props.labelPosition === "left" ? "is-row" : "is-column");

const wrapperClasses = computed(() => formWrapperCva());

const subWrapperClasses = computed(() =>
  formSubWrapperCva({
    layout: layout.value,
    loading: props.loading,
    disabled: isDisabled.value,
    labelCenter: props.labelCenter,
    hasMargin: props.hasMargin,
  }),
);

const labelClasses = computed(() =>
  formLabelCva({
    layout: layout.value,
  }),
);

const requiredClasses = computed(() => formRequiredCva());
</script>

<template>
  <div :class="wrapperClasses">
    <div :class="subWrapperClasses">
      <label
        v-if="label"
        :id="labelId"
        :class="labelClasses"
        :for="mode === 'palette' ? undefined : id"
      >
        {{ label }}
        <span v-if="required" :class="requiredClasses">*</span>
      </label>
      <div :style="{ width: layout === 'is-row' ? 'calc(100% - 33.33%)' : '100%' }">
        <!-- Palette mode -->
        <template v-if="mode === 'palette'">
          <VSkeleton
            v-if="loading"
            :height="40"
            :width="100"
            width-unit="%"
            :dynamic-width="false"
            class="rounded"
            :pill="false"
          />
          <div
            v-else
            :id="id"
            role="radiogroup"
            :aria-labelledby="label ? labelId : undefined"
            :aria-disabled="isDisabled || undefined"
            class="grid gap-1.5"
            :style="{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }"
          >
            <button
              v-for="(color, index) in presetColors"
              :key="color"
              :ref="(element) => setRadioRef(element, index)"
              type="button"
              role="radio"
              :aria-checked="modelValue === color"
              :aria-label="colorName(color)"
              :title="colorName(color)"
              :tabindex="radioTabIndex(index)"
              class="w-6 h-6 rounded-md border-2 transition-all duration-150 hover:scale-110 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-primary-500 dark:focus-visible:ring-offset-dark-800"
              :class="[
                modelValue === color
                  ? 'border-gray-800 dark:border-white ring-2 ring-offset-1 ring-primary-500'
                  : 'border-transparent hover:border-gray-300 dark:hover:border-dark-500',
                isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              ]"
              :style="{ backgroundColor: color }"
              :disabled="isDisabled"
              @click="selectColor(color)"
              @keydown="onRadioKeydown($event, index)"
            />
          </div>
        </template>

        <!-- Picker mode (default) -->
        <ACTooltip
          v-else
          type="edit_field"
          :allow-change="hasPerms"
        >
          <VSkeleton
            v-if="loading"
            :height="40"
            :width="100"
            width-unit="%"
            :dynamic-width="false"
            class="rounded"
            :pill="false"
          />
          <div
            v-else
            class="relative flex items-center rounded-full border border-solid px-2.5 w-full dark:bg-dark-800 has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-primary-500"
            :class="[
              hasError ? 'border-red-500' : 'border-gray-300 dark:border-dark-600',
              variant,
              size === 'medium' ? 'h-8 text-sm' : 'h-10 text-base',
              isDisabled ? 'cursor-not-allowed opacity-50' : '',
            ]"
          >
            <span
              class="w-5 h-5 rounded-lg border border-solid border-gray-300 dark:border-dark-600 mr-2.5 shrink-0"
              aria-hidden="true"
              :style="{ backgroundColor: modelValue || defaultColor || '#E0E0E0' }"
            />
            <input
              :id="id"
              type="color"
              :disabled="isDisabled"
              class="absolute inset-0 opacity-0 cursor-pointer w-full"
              :value="modelValue || defaultColor || '#E0E0E0'"
              :aria-invalid="computedAriaInvalid"
              :aria-describedby="computedAriaDescribedby"
              @input="handleColorInput"
              @blur="$emit('blur')"
              @change="$emit('change')"
            >
            <span
              :id="hasError ? errorId : undefined"
              class="grow min-w-0 truncate text-sm pointer-events-none"
              :class="hasError
                ? 'text-red-500'
                : isPlaceholder ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'"
            >
              {{ hasError ? errorMessage : displayValue }}
            </span>
            <button
              v-if="modelValue !== emptyValue"
              type="button"
              class="p-1.5 rounded-full text-gray-400 hover:text-red-500 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors duration-300 relative z-10"
              :aria-label="t('base.clear')"
              :disabled="isDisabled"
              @click.stop="clearColor"
            >
              <VIcon name="x" aria-hidden="true" />
            </button>
          </div>
        </ACTooltip>
      </div>
    </div>
  </div>
</template>
